// Wrappers around the admin user-management endpoints (FIN-21) and the
// invitation admin endpoints (FIN-18) consumed by the /settings/users page.
// Trusted-device management (FIN-22) for the signed-in user also lives here
// since it shares the same error-parsing + authedFetch wiring.

import { AuthApiError } from "./api";
import { authedFetch } from "./http";

async function parseError(res: Response): Promise<AuthApiError> {
  let code = `http_${res.status}`;
  let message: string | null = null;
  try {
    const body = (await res.json()) as {
      error?: unknown;
      message?: unknown;
    };
    if (typeof body.error === "string") code = body.error;
    if (typeof body.message === "string") message = body.message;
  } catch {
    // body wasn't JSON
  }
  return new AuthApiError(code, res.status, message);
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

async function readEmpty(res: Response): Promise<void> {
  if (!res.ok) throw await parseError(res);
}

export type UserRole = "admin" | "user";
export type UserStatus = "active" | "disabled";

export interface AdminUserRow {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  totp_enabled: number;
  last_login_at: string | null;
  created_at: string;
}

export interface ListUsersResult {
  users: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListUsersParams {
  q?: string;
  role?: UserRole;
  status?: UserStatus;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}

export async function listUsers(
  params: ListUsersParams = {},
): Promise<ListUsersResult> {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.role) search.set("role", params.role);
  if (params.status) search.set("status", params.status);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const qs = search.toString();
  const res = await authedFetch(`/admin/users${qs ? `?${qs}` : ""}`, {
    signal: params.signal,
  });
  return readJson<ListUsersResult>(res);
}

export interface UpdateUserInput {
  name?: string;
  role?: UserRole;
  status?: UserStatus;
}

export async function updateUser(
  id: string,
  input: UpdateUserInput,
): Promise<AdminUserRow> {
  const res = await authedFetch(`/admin/users/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return readJson<AdminUserRow>(res);
}

export async function deleteUser(id: string): Promise<void> {
  const res = await authedFetch(`/admin/users/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  await readEmpty(res);
}

export async function forceLogoutUser(id: string): Promise<void> {
  const res = await authedFetch(
    `/admin/users/${encodeURIComponent(id)}/force-logout`,
    { method: "POST" },
  );
  await readEmpty(res);
}

export async function reset2faForUser(id: string): Promise<void> {
  const res = await authedFetch(
    `/admin/users/${encodeURIComponent(id)}/reset-2fa`,
    { method: "POST" },
  );
  await readEmpty(res);
}

export async function sendPasswordResetForUser(id: string): Promise<void> {
  const res = await authedFetch(
    `/admin/users/${encodeURIComponent(id)}/send-password-reset`,
    { method: "POST" },
  );
  await readEmpty(res);
}

export type InvitationStatus = "pending" | "used" | "expired";

export interface AdminInvitationRow {
  id: string;
  email: string;
  role: UserRole;
  expires_at: string;
  used_at: string | null;
  invited_by: string;
  created_at: string;
  status: InvitationStatus;
}

export async function listInvitations(
  signal?: AbortSignal,
): Promise<AdminInvitationRow[]> {
  const res = await authedFetch(`/admin/invitations`, { signal });
  return readJson<AdminInvitationRow[]>(res);
}

export interface CreatedInvitation {
  id: string;
  email: string;
  role: UserRole;
  expires_at: string;
}

export async function createInvitation(input: {
  email: string;
  role: UserRole;
}): Promise<CreatedInvitation> {
  const res = await authedFetch(`/admin/invitations`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return readJson<CreatedInvitation>(res);
}

export async function revokeInvitation(id: string): Promise<void> {
  const res = await authedFetch(`/admin/invitations/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  await readEmpty(res);
}

export interface TrustedDevice {
  id: string;
  user_agent: string | null;
  last_ip: string | null;
  expires_at: string;
  last_used_at: string | null;
  created_at: string;
  current: boolean;
}

export async function listTrustedDevices(
  signal?: AbortSignal,
): Promise<TrustedDevice[]> {
  const res = await authedFetch(`/auth/2fa/devices`, { signal });
  const body = await readJson<{ devices: TrustedDevice[] }>(res);
  return body.devices;
}

export async function revokeTrustedDevice(id: string): Promise<void> {
  const res = await authedFetch(`/auth/2fa/devices/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  await readEmpty(res);
}
